import { Box, ButtonGroup, Grid } from '@material-ui/core';
import { formatCurrency, formatPercentage } from '@roc/ui/utils';
import {
  Button, Tooltip,
} from '@roc/ui';
import { observer } from 'mobx-react';
import { SelectedLtvSlider } from './loanToValueSlider'
import { ItemDisplay } from '../../properties/borrowerLoanSubmission/components/itemDisplay'
import { makeStyles, Theme } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import { CheckBox, CheckBoxOutlineBlank } from '@material-ui/icons';

interface Props {
  store: any;
  quote: any;
  loanEconomicsStore: any;
  quoteIndex?: number;
  onNext: () => void;
  onDownload: () => void;
}

const styles: any = (theme: Theme) => ({
  container: {
    border: '1px solid rgba(0, 0, 0, 0.38)',
    borderRadius: 14,
    paddingLeft: 26,
    paddingRight: 26,
    paddingTop: 26,
    paddingBottom: 26,
    marginTop: 16,
    marginBottom: 16,
    boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25);'
  },
  customizeButton: {
    borderRadius: '6px',
    color: theme.palette.primary.main,
    backgroundColor: 'white',
    width: 'auto',
    height: '40px',
    paddingTop: '8px',
    paddingBottom: '8px',
    fontSize: '14px',
    paddingLeft: '32px',
    paddingRight: '32px',
    letterSpacing: '1.25px',
    textTransform: 'uppercase'
  },
  chooseOptionButton: {
    border: `1px solid ${theme.palette.primary.main}`,
  },
  rate: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  rateValue: {
    fontSize: '20px',
    fontWeight: 500,
  },
  rateLabel: {
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '10px'
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '25px'
  },
  buttonText: {
    marginLeft: '8px',
    letterSpacing: '1.25px',
  },
  bigLabel: {
    fontSize: '32px',
  },
  removeQuoteButton: {
    color: '#00BCD4',
    borderRadius: '6px',
    border: '1px solid rgba(0, 0, 0, 0.23)',
    backgroundColor: 'white',
    width: 'auto',
    height: '40px',
    paddingTop: '8px',
    paddingBottom: '8px',
    fontSize: '14px',
    paddingLeft: '32px',
    paddingRight: '32px',
    letterSpacing: '1.25px',
    textTransform: 'uppercase'
  },
  leftSideButtonContainer: {
    display: 'flex',
    flexDirection: 'column',
  }
});

const useStyles = makeStyles(styles);



export const CustomizeYourQuote = observer(({ store, quote, quoteIndex, loanEconomicsStore, onNext, onDownload }: Props) => {
  const formStore = store;
  const classes = useStyles();
  const loanToValue = formStore.form.fields.loanToValue.value ?? 0;
  const isQuoteSelected = loanEconomicsStore.selectedQuotes.includes(quote);
  const selectQuote = () => {
    loanEconomicsStore.toggleCustomQuote(quote);
  }
  const setSelectedOption = () => {
    loanEconomicsStore.setQuote(quote);
    onNext()
  }
  const removeQuote = () => {
    loanEconomicsStore.removeDisplayedQuote(quote)
  }
  return (
    <Grid xs={12} container className={classes.container}>
      <Grid item xs={12}>
        <Box className={classes.buttonsContainer}>
          <Box className={classes.leftSideButtonContainer}>
            <Tooltip
              title="You can select up to 3 quotes to compare side by side and download"
              placement="top"
            >
              <ButtonGroup
                color="primary"
                size="small"
                style={{
                  margin: '8px 0',
                }}
              >
                <Button
                  variant="outlined"
                  testId="compareCheckBox"
                  // disabled={!goodToSubmitQuote}
                  onClick={e => {
                    e.stopPropagation();
                    selectQuote();
                  }}
                >
                  {isQuoteSelected ? <CheckBox /> : <CheckBoxOutlineBlank />}
                </Button>
                <Button
                  variant="outlined"
                  testId="compareButton"
                  className={classes.customizeButton}
                  // disabled={!goodToSubmitQuote}
                  onClick={e => {
                    if (!isQuoteSelected) {
                      selectQuote();
                    }
                    onDownload();
                  }}
                  style={{
                    textTransform: 'unset',
                  }}
                >
                  COMPARE / DOWNLOAD
                </Button>
              </ButtonGroup>
            </Tooltip>
            <Button
              variant="outlined"
              testId="compareButton"
              className={classes.removeQuoteButton}
              onClick={e => {
                removeQuote();
              }}
              style={{
                textTransform: 'unset',
              }}
            ><DeleteIcon />
              Remove Quote
            </Button></Box>
          <Button
            size="small"
            variant="contained"
            className={`${classes.customizeButton} ${classes.chooseOptionButton}`}
            onClick={() => {
              setSelectedOption()
            }}
            testId="createCustomQuote"
          >
            Choose Option
          </Button>
        </Box>
      </Grid>
      <Grid className={classes.rate} item xs={4}>
        <ItemDisplay
          label={<Box className={classes.rateLabel}>RATE</Box>}
          value={
            <Box className={`${classes.rateValue} ${classes.bigLabel}`}>
              {formatPercentage(quote?.rate)}
            </Box>
          }
        ></ItemDisplay>
      </Grid>
      <Grid item xs={8}>
        <SelectedLtvSlider
          store={store}
          loanEconomicsStore={loanEconomicsStore}
          quote={quote}
          quoteIndex={quoteIndex}
        />
      </Grid>
      <Grid className={classes.rate} item xs={4}>
        <ItemDisplay
          label={
            <Box className={classes.rateLabel}>
              MONTHLY PAYMENT (PI PAYMENT)
            </Box>
          }
          value={
            <Box className={classes.rateValue}>
              {formatCurrency(quote?.pricerLoanTerms?.monthlyPaymentWithoutExpenses ?? 0)}
            </Box>
          }
        ></ItemDisplay>
      </Grid>
      <Grid className={classes.rate} item xs={4}>
        <ItemDisplay
          label={
            <Box className={classes.rateLabel}>ORIGINATION POINTS AND FEES</Box>
          }
          value={
            <Box className={classes.rateValue}>
              {formatCurrency(
                (quote?.pointsAndFeesPercent * quote.pricerLoanTerms.amount) /
                100 + loanEconomicsStore?.borrowerSubmissionFees?.totalAddOnFee
                + quote?.addOnPointsAmount
              )}
            </Box>
          }
        ></ItemDisplay>
      </Grid>
      <Grid className={classes.rate} item xs={4}>
        <ItemDisplay
          label={<Box className={classes.rateLabel}>PITIA DSCR</Box>}
          value={
            <Box className={classes.rateValue}>
              {quote?.dscr?.pitiaDscrInIOPeriod ?? '-'}
            </Box>
          }
        ></ItemDisplay>
      </Grid>
    </Grid>
  );
});